import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, Button, Card, CardActions, CardContent, IconButton } from '@mui/material'
import { eachDayOfInterval, format, isSameDay } from 'date-fns'
import Slot from './Slot'
import { Add } from '@mui/icons-material'
import { useSelector } from 'react-redux'
import { ACTIVITY_COLLECTION, AIRCRAFT_COLLECTION, REQUEST_COLLECTION, SLOT_COLLECTION, USER_COLLECTION } from '../../_constants/globals'
import Loading from '../_library/Loading'
import InstructorSelect from './InstructorSelect'
import { fr } from 'date-fns/locale'
import { head, last } from 'lodash'
import Request from './Request'
import { useFirestore } from '../../hooks/useFirestore'
import useAuth from '../../hooks/useAuth'
import { arrayUnion } from 'firebase/firestore'


const PlanningCard = ({ sx }) => {

  const activityId = useSelector(state => state.data.selectedActivityId)
  const activityHooks = useFirestore(ACTIVITY_COLLECTION)
  const dbActivities = activityHooks.getDocs()
  const slotHooks = useFirestore(SLOT_COLLECTION)
  const slots = slotHooks.getDocs()
  const requestHooks = useFirestore(REQUEST_COLLECTION)
  const requests = requestHooks.getDocs()
  const aircraftHooks = useFirestore(AIRCRAFT_COLLECTION)
  const dbAircrafts = aircraftHooks.getDocs()
  const profile = useAuth().getProfile()
  const adminMode = useSelector(state => state.data.adminMode)
  const userHooks = useFirestore(USER_COLLECTION)

  const [editMode, setEditMode] = useState(false)

  const activity = useMemo(() => dbActivities?.find(a => a.id === activityId), [dbActivities, activityId])

  const days = useMemo(
    () => activity.firstDayDate && activity.lastDayDate && eachDayOfInterval({ start: activity.firstDayDate, end: activity.lastDayDate }),
    [activity.firstDayDate, activity.lastDayDate],
  )

  const aircrafts = useMemo(
    () => dbAircrafts?.filter(a => activity.aircraftRefs.map(({ id }) => id).includes(a.id)),
    [dbAircrafts, activity.aircraftRefs],
  )

  const handleSlotCreate = (day, period, aircraftId) => slotHooks.addDoc({
    activityRef: activityHooks.getDocRef(activityId),
    aircraftRef: aircraftHooks.getDocRef(aircraftId),
    day,
    period,
  })

  const handleAcceptRequest = request => {
    const slot = slots.find(s =>
      (isSameDay(days[Math.floor(s.day) - 1], request.dayDate) || request.period === 'inbound' || request.period === 'outbound')
      && s.period === request.period
      && s.aircraftRef?.id === request.aircraftRef.id
      && !s.userRef,
    )
    if (slot) slotHooks.updateDoc(slot.id, { userRef: request.userRef })
    if (slot && (!activity.pilotRefs || !activity.pilotRefs.some(ref => ref.id === request.userRef.id)))
      activityHooks.updateDoc(activity.id, { pilotRefs: arrayUnion(userHooks.getDocRef(request.userRef.id)) })
  }

  if (!aircrafts) return <Loading />
  else return (
    <Card sx={sx}>
      <CardContent>
        <Box component='table' sx={{
          borderCollapse: 'collapse',
          '& td': { borderLeft: '1px solid black' },
          width: '100%',
        }}>
          <thead>
            <tr>
              <th colSpan={2} />
              <th>FI</th>
              {aircrafts.map(aircraft => <th key={aircraft.id}>{aircraft.name}</th>)}
            </tr>
          </thead>
          <tbody>
            <Box component='tr' sx={{ borderTop: '1px solid black' }}>
              <th colSpan={2}>Convoyage</th>
              <td></td>
              {aircrafts.map(aircraft => <td key={'inbound'+aircraft.name}>
                <div>
                  {slots?.filter(s => typeof s.day !== 'number' && s.period === 'inbound' && s.aircraftRef?.id === aircraft.id).map((s, i) => <Slot key={i} disabled={!editMode} {...s} />)}
                  {editMode && <IconButton title='ajouter un créneau' onClick={() => handleSlotCreate(head(days), 'inbound', aircraft.id)} sx={{ p: 0 }}><Add /></IconButton>}
                </div>
                <div>
                  {requests?.filter(r => r.aircraftRef?.id === aircraft.id && isSameDay(r.dayDate, head(days)) && r.period === 'inbound').map(r =>
                    <Request key={r.id} onClick={() => handleAcceptRequest(r)} disabled={!editMode} {...r} />,
                  )}
                </div>
              </td>)}
            </Box>
            {days.reduce((acc, val) => [...acc, { day: val, period: 'AM' }, { day: val, period: 'PM' }], []).map(({ day, period }, index) =>
              <Box key={day.getTime()+period} component='tr' sx={{ borderTop: period ==='AM' && '1px solid black' }}>
                {period ==='AM' && <th rowSpan={2}>{format(day, 'EEEE', { locale: fr })}</th>}
                <td style={{ border: 'none' }}>{period}</td>
                <td><InstructorSelect day={day} period={period} disabled={!editMode} /></td>
                {aircrafts.map(aircraft => <td key={aircraft.name + period}>
                  <div>
                    {slots?.filter(s => s.day === index / 2 + 1 && s.period === period && s.aircraftRef?.id === aircraft.id).map((s, i) => <Slot key={i} disabled={!editMode} {...s} />)}
                    {editMode && <IconButton title='ajouter un créneau' onClick={() => handleSlotCreate(index / 2 + 1, period, aircraft.id)} sx={{ p: 0 }}><Add /></IconButton>}
                  </div>
                  <div>
                    {requests?.filter(r => r.aircraftRef?.id === aircraft.id && isSameDay(r.dayDate, day) && r.period === period).map(r =>
                      <Request key={r.id} onClick={() => handleAcceptRequest(r)} disabled={!editMode} {...r} />,
                    )}
                  </div>
                  {period === 'AM' && <Box sx={{ height: '8px' }} />}
                </td>)}
              </Box>,
            )}
            <Box component='tr' sx={{ borderTop: '1px solid black' }}>
              <th colSpan={2}>Convoyage</th>
              <td></td>
              {aircrafts.map(aircraft => <td key={'outbound'+aircraft.name}>
                <div>
                  {slots?.filter(s => typeof s.day !== 'number' && s.period === 'outbound' && s.aircraftRef?.id === aircraft.id).map((s, i) => <Slot key={i} disabled={!editMode} {...s} />)}
                  {editMode && <IconButton title='ajouter un créneau' onClick={() => handleSlotCreate(last(days), 'outbound', aircraft.id)} sx={{ p: 0 }}><Add /></IconButton>}
                </div>
                <div>
                  {requests?.filter(r => r.aircraftRef?.id === aircraft.id && r.period === 'outbound').map(r =>
                    <Request key={r.id} onClick={() => handleAcceptRequest(r)} disabled={!editMode} {...r} />,
                  )}
                </div>
              </td>)}
            </Box>
          </tbody>
        </Box>
      </CardContent>
      <CardActions>
        {(adminMode || activity.organizerRef?.id === profile.id) && <Button fullWidth onClick={() => setEditMode(prev => !prev)}>
          {editMode ? 'Sauvegarder' : 'Modifier'}
        </Button>}
      </CardActions>
    </Card>
  )
}

PlanningCard.propTypes = {
  activity: PropTypes.object,
  sx: PropTypes.object,
}

export default PlanningCard
